/**
 * One message bubble in a roundtable thread (§7h).
 *
 * Similar to single-chat Message.jsx but with per-message speaker
 * attribution: persona name + avatar header above the bubble, a thin
 * left border tinted with the panel-accent colour assigned by the
 * speaker's index in the panel (1st speaker = --panel-accent-1, etc).
 *
 * Colour is secondary reinforcement — the primary attribution is the
 * persona name + avatar — so colour-blind users don't lose
 * information. The accent is applied via a CSS modifier class from
 * the accentIndex prop; callers (RoundtableThread) compute the
 * index from the personaId's position in the conversation's
 * stored personaIds array.
 *
 * User messages render as a plain bubble (same as single chat); no
 * attribution header.
 */

import ReactMarkdown from "react-markdown";
import Avatar from "./Avatar.jsx";
import ErrorBubble from "./ErrorBubble.jsx";

export default function RoundtableMessage({
  role,
  content,
  persona, // full persona object, may be undefined if not yet loaded
  accentIndex, // 1-based index into the panel; determines the accent colour
  streaming = false,
  error = null,
}) {
  if (role === "user") {
    return (
      <div className="roundtable-message roundtable-message-user">
        <div className="message-bubble">
          <p>{content}</p>
        </div>
      </div>
    );
  }

  // Assistant message: attributed speaker strip + bubble.
  const accentClass = accentIndex
    ? `roundtable-message-accent-${accentIndex}`
    : "";

  return (
    <div className={`roundtable-message roundtable-message-assistant ${accentClass}`}>
      <div className="roundtable-message-speaker">
        {persona && <Avatar persona={persona} />}
        <div className="roundtable-message-speaker-info">
          <span className="roundtable-message-name">
            {persona?.name || "Unknown panellist"}
          </span>
          {persona?.category && (
            <span className="roundtable-message-category">
              {persona.category}
            </span>
          )}
        </div>
      </div>
      <div className="message-bubble">
        {error ? (
          <ErrorBubble message={error} />
        ) : (
          <>
            <ReactMarkdown>{content || ""}</ReactMarkdown>
            {streaming && !content && (
              <div className="typing-indicator">
                <span />
                <span />
                <span />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
